import type { RequestOptions } from '@@/plugin-request/request';
import type { RequestConfig } from '@umijs/max';
import { history, request } from '@umijs/max';
import { message, notification } from 'antd';

// Token 刷新状态管理
let isRefreshing = false;
let refreshPromise: Promise<any> | null = null;
// 等待刷新完成的 Promise
const waitRefresh = () => {
  if (!refreshPromise) {
    const refresh_token = localStorage.getItem('refresh_token');
    if (!refresh_token) {
      return Promise.reject(new Error('No refresh token'));
    }
    refreshPromise = request('/auth/refresh', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      data: { refresh_token },
      skipErrorHandler: true,
      skipToken: true,
    })
      .then((response: any) => {
        // 兼容两种响应格式：直接返回 Token 或包装在 data 中
        const tokenData = response?.data || response;
        const accessToken = tokenData?.access_token;
        const refreshTokenValue = tokenData?.refresh_token;

        if (accessToken) {
          localStorage.setItem('access_token', accessToken);
          if (refreshTokenValue) {
            localStorage.setItem('refresh_token', refreshTokenValue);
          }
          return accessToken;
        }
        throw new Error('Token refresh failed - no access_token in response');
      })
      .catch((err) => {
        // 刷新失败，清除 token
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        history.push('/user/login');
        throw err;
      })
      .finally(() => {
        refreshPromise = null;
        isRefreshing = false;
      });
  }
  return refreshPromise;
};

// 错误处理方案： 错误类型
enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}
// 与后端约定的响应数据格式
interface ResponseStructure {
  success: boolean;
  data: any;
  errorCode?: number;
  errorMessage?: string;
  showType?: ErrorShowType;
  code?: number;
}

/**
 * @name 错误处理
 * pro 自带的错误处理， 可以在这里做自己的改动
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {
  // 错误处理： umi@3 的错误处理方案。
  errorConfig: {
    // 错误抛出
    errorThrower: (res) => {
      // 支持两种响应格式：
      // 1. 最外层 success: { success, data, errorCode, errorMessage }
      // 2. 后端实际返回: { code, message, data, success }
      const { success, data, errorCode, errorMessage, showType, code } = res as unknown as ResponseStructure;

      // 如果最外层有 success 字段且为 false，抛出业务错误
      if (success === false) {
        const error: any = new Error(errorMessage || '请求失败');
        error.name = 'BizError';
        error.info = { errorCode, errorMessage, showType, data };
        throw error;
      }

      // 如果有 code 字段且不是 200，抛出业务错误
      if (code && code !== 200) {
        const error: any = new Error(errorMessage || `请求失败，错误码: ${code}`);
        error.name = 'BizError';
        error.info = { errorCode: code, errorMessage, showType, data };
        throw error;
      }
    },
    // 错误接收及处理
    errorHandler: (error: any, opts: any) => {
      if (opts?.skipErrorHandler) throw error;

      // 我们的 errorThrower 抛出的错误。
      if (error.name === 'BizError') {
        const errorInfo: ResponseStructure | undefined = error.info;
        if (errorInfo) {
          const { errorMessage, errorCode } = errorInfo;
          switch (errorInfo.showType) {
            case ErrorShowType.SILENT:
              // do nothing
              break;
            case ErrorShowType.WARN_MESSAGE:
              message.warning(errorMessage);
              break;
            case ErrorShowType.ERROR_MESSAGE:
              message.error(errorMessage);
              break;
            case ErrorShowType.NOTIFICATION:
              notification.open({
                description: errorMessage,
                message: errorCode,
              });
              break;
            case ErrorShowType.REDIRECT:
              // TODO: redirect
              break;
            default:
              message.error(errorMessage);
          }
        }
      } else if (error.response) {
        // Axios 的错误
        // 请求成功发出且服务器也响应了状态码，但状态代码超出了 2xx 的范围

        // 处理 401 未授权错误 - 全局 token 刷新逻辑
        if (error.response.status === 401) {
          const originalRequest = error.config;

          // 避免无限重试
          if (originalRequest._retry) {
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            history.push('/user/login');
            throw error;
          }
          originalRequest._retry = true;

          // 刷新 token 并重试原始请求
          return waitRefresh().then((newToken) => {
            // 构建新的请求配置，确保 headers 被正确传递
            const retryConfig = {
              method: originalRequest.method,
              headers: {
                ...originalRequest.headers,
                Authorization: `Bearer ${newToken}`,
              },
              data: originalRequest.data,
              params: originalRequest.params,
              skipErrorHandler: true, // 避免重试请求再次进入错误处理
            };

            // 重试原始请求
            return request(originalRequest.url, retryConfig);
          }).catch((err) => {
            throw err;
          });
        }

        // 不在全局处理器中显示错误消息，让组件自己处理
        // 抛出错误，让组件的 catch 块处理
        throw error;
      } else if (error.request) {
        // 请求已经成功发起，但没有收到响应
        // \`error.request\` 在浏览器中是 XMLHttpRequest 的实例，
        // 而在node.js中是 http.ClientRequest 的实例
        message.error('None response! Please retry.');
      } else {
        // 发送请求时出了点问题
        message.error('Request error, please retry.');
      }
      return;
    },
  },

  // 请求拦截器
  requestInterceptors: [
    async (config: RequestOptions) => {
      // 检查是否明确标记为不需要 token（使用特殊标记）
      const skipToken = (config as any).skipToken;
      if (skipToken) {
        return { ...config };
      }

      // 从 localStorage 获取 token
      let token = localStorage.getItem('access_token');

      // 如果没有 token 且有 refresh_token，且不是 /auth/refresh 请求，尝试刷新
      const isRefreshRequest = config.url?.includes('/auth/refresh');
      if (!token && localStorage.getItem('refresh_token') && !isRefreshRequest && !(config as any)._skipAutoRefresh) {
        try {
          // 标记此请求，避免递归刷新
          (config as any)._skipAutoRefresh = true;
          const newToken = await waitRefresh();
          token = newToken;
        } catch (err) {
          // 刷新失败，继续发起请求（会在 errorHandler 中处理）
        }
      }

      // 如果有 token，添加到请求头
      if (token) {
        // 添加租户视角 header（系统管理员切换租户视角时使用）
        const tenantViewId = localStorage.getItem('tenant_view_id');
        const headers = {
          ...config.headers,
          Authorization: `Bearer ${token}`,
          ...(tenantViewId ? { 'X-Tenant-View-Id': tenantViewId } : {}),
        };

        return { ...config, headers };
      }

      return { ...config };
    },
  ],

  // 响应拦截器
  responseInterceptors: [
    (response) => {
      // 拦截响应数据，进行个性化处理
      const status = response.status;
      const responseData = response.data as any;

      // 检查响应状态
      if (status >= 200 && status < 300) {
        // HTTP 成功，检查业务成功字段
        if (responseData?.success === false) {
          // 业务失败，由 errorThrower 处理
        }
      }

      return response;
    },
  ],
};
